import React from "react";
import styles from "./DressingChangeFrequency.module.scss";
import { signedLockedAccess } from "../../../../../utils";
import { CANCEL_BTN, CANCEL_ID, SAVE_BTN, SUBMIT_ID } from "../../../../../constants";
import constants from "../../../../../constants/constants.json";
import ConfirmationModal from "../../../../secondary/ConfirmationModal/ConfirmationModal";
import { Form } from "../../../../primary";
import { EditActionButton } from "../../../../secondary/EditActionButton";
import { FormActions } from "../../../../secondary/FormActions";
import { PERMISSIONS } from "../../../../../constants/permissions";
import { useDressingChangeFrequencyDesign } from "./hooks/useDressingChangeFrequencyDesign";
import { DressingChangeFrequencyCard } from "./DressingChangeFrequencyCard";
import { DEFAULT_MAX_DAYS } from "./utils/scheduleUtils";
import { SectionBanners, StatusBanner } from "../../../../secondary";
import { DressingChangeFrequencySkeleton } from "./components/DressingChangeFrequencySkeleton";

interface AssessmentDetails {
  assessmentStage?: string;
}

interface DressingChangeFrequencyProps {
  interventionId: string | number;
  assessmentDetails?: AssessmentDetails;
}

export const DressingChangeFrequency: React.FC<DressingChangeFrequencyProps> = ({ interventionId, assessmentDetails }) => {
  const hook = useDressingChangeFrequencyDesign({ interventionId });

  const primaryItems = hook.dressChange?.data?.primary?.productCategory ?? [];
  const secondaryItems = hook.dressChange?.data?.secondary?.productCategory ?? [];
  const isEmpty = primaryItems.length === 0 && secondaryItems.length === 0;

  return (
    <>
      {hook.confirmationModal && (
        <ConfirmationModal message={constants.confirmation} onClose={hook.confirmationClose} onConfirm={hook.yesConfirmationModalClick} />
      )}

      <SectionBanners />

      <div className={styles.container}>
        {hook.isLoading ? (
          <DressingChangeFrequencySkeleton />
        ) : isEmpty ? (
          <StatusBanner status="info" message={constants.patient.noDressingSelected} layout="inline" />
        ) : (
          <Form
            setFormChangesConfirm={hook.setFormChangesConfirm}
            className="col-12 p-0"
            onSubmit={hook.handleSubmit(hook.doSubmit)}
            requiredPermission={PERMISSIONS.TREATMENT}
          >
            <div className={styles.header_row}>
              <div className={styles.header}>
                <h2 className={styles.title}>Dressing change frequency</h2>
                <p className={styles.subtitle}>
                  Set how often each dressing is changed — on an interval (every 5 hours, every 5 days), a count per period (3 times a week), fixed
                  weekdays, or as needed.
                </p>
              </div>

              {!hook.editable && (primaryItems.length > 0 || secondaryItems.length > 0) && (
                <EditActionButton onClick={hook.handleEdit} disabled={!hook.hasPermission(PERMISSIONS.TREATMENT_EDIT) || !signedLockedAccess(assessmentDetails?.assessmentStage)} />
              )}
            </div>

            {primaryItems.length > 0 && (
              <section className={styles.section}>
                <h3 className={styles.section_title}>Primary Dressing</h3>
                {primaryItems.map((item, index) => (
                  <DressingChangeFrequencyCard
                    key={item.key ?? index}
                    item={item}
                    type="primary"
                    index={index}
                    editable={hook.editable}
                    schedule={hook.getSchedule("primary", index)}
                    maxDays={item.recommended ?? DEFAULT_MAX_DAYS}
                    onChange={(patch) => hook.updateSchedule("primary", index, patch)}
                    onToggleDay={(day) => hook.toggleWeekday("primary", index, day)}
                  />
                ))}
              </section>
            )}

            {secondaryItems.length > 0 && (
              <section className={styles.section}>
                <h3 className={styles.section_title}>Secondary Dressing</h3>
                {secondaryItems.map((item, index) => (
                  <DressingChangeFrequencyCard
                    key={item.key ?? index}
                    item={item}
                    type="secondary"
                    index={index}
                    editable={hook.editable}
                    schedule={hook.getSchedule("secondary", index)}
                    maxDays={item.recommended ?? DEFAULT_MAX_DAYS}
                    onChange={(patch) => hook.updateSchedule("secondary", index, patch)}
                    onToggleDay={(day) => hook.toggleWeekday("secondary", index, day)}
                  />
                ))}
              </section>
            )}

            {hook.editable && (
              <FormActions
                primaryLabel={SAVE_BTN}
                primaryType="submit"
                primaryId={SUBMIT_ID}
                primaryLoading={hook.isSaving}
                secondaryLabel={CANCEL_BTN}
                secondaryId={CANCEL_ID}
                onSecondaryClick={hook.handleCancel}
              />
            )}
          </Form>
        )}
      </div>
    </>
  );
};
